import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { mixpanel, logger } from '@fiverr-private/obs';
import { Container, Section, Stack } from '@fiverr-private/layout_components';
import { useImpressionManager } from '@fiverr-private/impressionable';
import { Box } from '@fiverr-private/theme';
import { ErrorBoundary, LazyComponent } from '@fiverr-private/ui_utils';
import { DEFAULT_FIVERR_LOCALE } from '@fiverr-private/localization';
import { getContext } from '@fiverr-private/fiverr_context';
import {
    MIX_PANEL_PAGE_NAME,
    shouldShowHighSpendPotentialJP,
    isEligibleForHighAspJP,
} from '@fiverr-private/homepage_utils';
import { PrimaryPositionEnum, ROLLOUTS } from '@fiverr-private/homepage_common_types';
import { Link } from '@fiverr-private/typography';
import { loadable } from '@fiverr-private/perseus';
import { PricingProvider } from '@fiverr-private/price_upfront_components';
import { TailoredHomepageInitialProps } from '../../../../lib/interfaces/initialProps';
import { ThemeContext } from '../../../context/theme/theme.context';
import { MetadataContext } from '../../../context/metadata/metadata.context';
import { UserProvider } from '../../../context/user/userProvider';
import { DrawerContext } from '../../../context/drawer/drawer.context';
import RowSections from '../../RowSections/RowSections';
import { useDrawer } from '../../../hooks/useDrawer';
import TabbedCarouselsSkeleton from '../../TabbedCarousels/TabbedCarouselsSkeleton';
import HeaderSection from '../../HeaderSection/HeaderSection';
import { UGCContext } from '../../../context/ugc/ugc.context';
import useTranslationToggle from '../../../hooks/useTranslationToggle';
import MachineTranslationButton from '../../MachineTranslationButton/MachineTranslationButton';
import { TranslationState } from '../../../hooks/useTranslationToggle.types';
import PrimarySection from '../../PrimarySection/PrimarySection';
import { isValidPrimarySection } from '../../PrimarySection/utils';
import { AppI18n } from '../../../i18n';
import type { HighAspJobPostProps } from '../../HighAspJobPost';

const HighAspJobPost: React.FC<HighAspJobPostProps> = loadable(
    () => import(/* webpackChunkName: 'HighAspJobPost' */ '../../HighAspJobPost/HighAspJobPost')
);

const PrimarySectionLazyImport = () =>
    import(/* webpackChunkName: 'PrimarySection' */ '../../PrimarySection/PrimarySection');

const BookPublishingProjectCarouselLazyImport = () =>
    import(
        /* webpackChunkName: 'BookPublishingProjectCarousel' */ '@fiverr-private/book_publishing_freelancers_carousel'
    ).then((module) => ({ default: module.BookPublishingFreelancersCarousel }));

const App = (props: TailoredHomepageInitialProps) => {
    useImpressionManager();
    const {
        pageLayout: { title, rowSections, layoutId, primary, secondary, ternary },
        buyerInsights,
        jobsInfo,
        userData,
        tabbedCarouselsMultipleSections,
        rollouts,
        currency,
        primariesToDisplay,
        secondariesToDisplay,
        ternariesToDisplay,
        bookPublishingProject,
        priceUpfrontConfiguration,
        priceUpfrontContext,
        isPriceUpfrontEnabled = false,
    } = props;
    const queryClient = new QueryClient();

    const {
        setChildren: setDrawerChildren,
        setSize: setDrawerSize,
        setDrawerCallbacks,
        openDrawer,
        isDrawerOpened,
        children: drawerChildren,
        size: drawerSize,
        closeDrawer,
    } = useDrawer();
    const { locale, abTests, userId, isMobile } = getContext();
    const shouldRenderPrimaries = isValidPrimarySection(primariesToDisplay, primary.ratio);
    const shouldRenderSecondaries = isValidPrimarySection(secondariesToDisplay, secondary.ratio);
    const shouldRenderTernaries = isValidPrimarySection(ternariesToDisplay, ternary.ratio);
    const showHighSpendPotentialJP =
        shouldShowHighSpendPotentialJP(abTests) && isEligibleForHighAspJP({ userId, isMobile, locale });

    useEffect(() => {
        mixpanel.trackPageView({
            'Page Name': MIX_PANEL_PAGE_NAME,
            'Layout Id': layoutId,
            'Buyer Preference': buyerInsights.segment.buyerPreference,
        });
    }, [layoutId, buyerInsights.segment.buyerPreference]);

    const { translationState, setTranslationState, showTranslatedContentRef, onTranslationToggle } =
        useTranslationToggle({
            personalizedContent: {
                sections: {
                    ...(tabbedCarouselsMultipleSections?.tabbedCarouselsData?.data ?? {}),
                    ...(tabbedCarouselsMultipleSections?.storeBuilderData?.data ?? {}),
                },
            },
        });

    const { ugcMap, showTranslatedContent } = translationState;

    const showTranslationButton = locale !== DEFAULT_FIVERR_LOCALE;

    const addUgcData = (updatedUgcMap: TranslationState['ugcMap']) => {
        setTranslationState((prevState: TranslationState) => ({
            ...prevState,
            ugcMap: {
                ...prevState.ugcMap,
                original: {
                    ...prevState.ugcMap.original,
                    personalizedContent: {
                        sections: {
                            ...prevState.ugcMap.original?.personalizedContent.sections,
                            ...updatedUgcMap.original?.personalizedContent.sections,
                        },
                    },
                },
                translated: {
                    ...prevState.ugcMap.translated,
                    personalizedContent: {
                        sections: {
                            ...prevState.ugcMap.translated?.personalizedContent.sections,
                            ...updatedUgcMap.translated?.personalizedContent.sections,
                        },
                    },
                },
            },
        }));
    };

    return (
        <ErrorBoundary onError={logger.error}>
            <QueryClientProvider client={queryClient}>
                <MetadataContext.Provider value={{ rollouts, currency }}>
                    <UGCContext.Provider
                        value={{ ugcMap, showTranslatedContent, addUgcData, showTranslatedContentRef }}
                    >
                        <ThemeContext.Provider value={{ layoutId }}>
                            <PricingProvider
                                priceUpfrontConfiguration={priceUpfrontConfiguration}
                                priceUpfrontContext={priceUpfrontContext}
                                isPriceUpfrontEnabled={isPriceUpfrontEnabled}
                            >
                                <UserProvider {...{ buyerInsights, userData }}>
                                    <DrawerContext.Provider
                                        value={{
                                            setDrawerChildren,
                                            setDrawerSize,
                                            setDrawerCallbacks,
                                            openDrawer,
                                            closeDrawer,
                                        }}
                                    >
                                        <HeaderSection title={title} jobsInfo={jobsInfo}>
                                            {showHighSpendPotentialJP && <HighAspJobPost currency={currency} />}
                                        </HeaderSection>
                                        <Section data-testid="bottom-section" paddingTop={{ default: '7', lg: '3' }}>
                                            <Stack
                                                gap="0"
                                                direction="column"
                                                alignItems={{
                                                    default: 'center',
                                                    sm: 'flexStart',
                                                }}
                                                paddingLeft="1"
                                                position="relative"
                                                data-testid="bottom-section-container"
                                            >
                                                {showTranslationButton && (
                                                    <MachineTranslationButton
                                                        onToggle={onTranslationToggle}
                                                        useStickyTranslation
                                                    />
                                                )}

                                                {shouldRenderPrimaries && (
                                                    <PrimarySection
                                                        primariesToDisplay={primariesToDisplay}
                                                        ratio={primary.ratio}
                                                        position={PrimaryPositionEnum.PRIMARY}
                                                    />
                                                )}
                                                <Box width="100%">
                                                    <LazyComponent
                                                        componentProps={{
                                                            tabbedCarouselsContent:
                                                                tabbedCarouselsMultipleSections.tabbedCarouselsData,
                                                            layoutId,
                                                        }}
                                                        lazyImport={() =>
                                                            import(
                                                                /* webpackChunkName: 'TabbedCarousels' */ '../../TabbedCarousels/TabbedCarousels'
                                                            )
                                                        }
                                                        placeholder={<TabbedCarouselsSkeleton />}
                                                    />
                                                </Box>
                                                {tabbedCarouselsMultipleSections.storeBuilderData && (
                                                    <>
                                                        <Box width="100%" marginTop="12">
                                                            <LazyComponent
                                                                componentProps={{
                                                                    tabbedCarouselsContent:
                                                                        tabbedCarouselsMultipleSections.storeBuilderData,
                                                                    layoutId,
                                                                    mobileView: true,
                                                                    withBackgroundColor: true,
                                                                    subtitleComponent: (
                                                                        <AppI18n
                                                                            k="personalized_tabs.subtitle.store_builder"
                                                                            templates={{
                                                                                link: (text = '') => (
                                                                                    <Link
                                                                                        href="https://www.fiverr.com/cp/build-your-store"
                                                                                        target="_blank"
                                                                                    >
                                                                                        {text}
                                                                                    </Link>
                                                                                ),
                                                                            }}
                                                                        />
                                                                    ),
                                                                }}
                                                                lazyImport={() =>
                                                                    import(
                                                                        /* webpackChunkName: 'TabbedCarousels' */ '../../TabbedCarousels/TabbedCarousels'
                                                                    )
                                                                }
                                                                placeholder={<TabbedCarouselsSkeleton />}
                                                            />
                                                        </Box>
                                                    </>
                                                )}
                                                {shouldRenderSecondaries && (
                                                    <>
                                                        <Box
                                                            marginBottom="12"
                                                            data-testid="personalized-tabs-spacing"
                                                        />
                                                        <LazyComponent
                                                            lazyImport={PrimarySectionLazyImport}
                                                            componentProps={{
                                                                primariesToDisplay: secondariesToDisplay,
                                                                ratio: secondary.ratio,
                                                                position: PrimaryPositionEnum.SECONDARY,
                                                            }}
                                                        />
                                                    </>
                                                )}
                                                {rowSections.length > 0 && <RowSections rowSections={rowSections} />}

                                                {shouldRenderTernaries && (
                                                    <>
                                                        <Box
                                                            marginBottom="12"
                                                            data-testid="personalized-tabs-spacing"
                                                        />
                                                        <LazyComponent
                                                            lazyImport={PrimarySectionLazyImport}
                                                            componentProps={{
                                                                primariesToDisplay: ternariesToDisplay,
                                                                ratio: ternary.ratio,
                                                                position: PrimaryPositionEnum.TERNARY,
                                                            }}
                                                        />
                                                    </>
                                                )}

                                                {bookPublishingProject &&
                                                    rollouts[ROLLOUTS.CHEETAHS_BOOK_PUBLISHING] && (
                                                        <Container width="100%" marginTop="12" marginBottom="10">
                                                            <LazyComponent
                                                                lazyImport={BookPublishingProjectCarouselLazyImport}
                                                                componentProps={{
                                                                    userDisplayName: userData.displayName,
                                                                    currency,
                                                                    project: bookPublishingProject,
                                                                }}
                                                            />
                                                        </Container>
                                                    )}
                                            </Stack>
                                        </Section>
                                        <LazyComponent
                                            componentProps={{
                                                size: drawerSize,
                                                isDrawerOpen: isDrawerOpened,
                                                closeDrawer,
                                                children: drawerChildren,
                                            }}
                                            lazyImport={() =>
                                                import(
                                                    /* webpackChunkName: 'DrawerWrapper' */ '../../DrawerWrapper/DrawerWrapper'
                                                )
                                            }
                                        />
                                    </DrawerContext.Provider>
                                </UserProvider>
                            </PricingProvider>
                        </ThemeContext.Provider>
                    </UGCContext.Provider>
                </MetadataContext.Provider>
            </QueryClientProvider>
        </ErrorBoundary>
    );
};

export default App;
